import { useToast } from "@chakra-ui/react";
import React from "react";

import useApi from "../../../shared/hooks/useApi";
import { VisitInstanceDailyNoteId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

/**
 * Downloading a daily note as its office's branded PDF.
 *
 * One hook rather than a handler at each call site: the same control appears in the
 * attestation modal, the note drawer and the attestations table, and three copies of
 * "fetch, then open" would drift the first time one of them needed a fix.
 *
 * The document is rendered by the server on first request and cached, so the first click
 * on a note is slower than the rest. That is why this tracks a pending id and the callers
 * can disable the control while it runs -- without it, an impatient second click renders
 * the same note twice.
 */
export function useDailyNoteDocumentDownload() {
  const { api } = useApi();
  const toast = useToast();
  const [pendingNoteId, setPendingNoteId] = React.useState<VisitInstanceDailyNoteId | null>(null);

  const downloadOne = async (visitInstanceDailyNoteId: VisitInstanceDailyNoteId) => {
    setPendingNoteId(visitInstanceDailyNoteId);

    try {
      const response = await api.get("./visit_daily_notes/:visitInstanceDailyNoteId/document", {
        path: { visitInstanceDailyNoteId },
      });

      // A new tab rather than navigating: the reviewer is mid-decision on the page
      // behind this, and replacing it with a PDF loses where they were.
      window.open(response.signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      // The server refuses outright for an office with no template configured, which is
      // a real answer and not a failure to report as one -- formatErrorResponse surfaces
      // its wording rather than "Request failed with status code 400".
      toast({
        title: "Could not download the daily note",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      });
    } finally {
      setPendingNoteId(null);
    }
  };

  return {
    downloadOne,
    /** True while THAT note is rendering, so one spinner does not disable every row. */
    isPending: (visitInstanceDailyNoteId: VisitInstanceDailyNoteId) =>
      pendingNoteId === visitInstanceDailyNoteId,
  };
}
